<template>
  <v-menu
    ref="menu"
    :close-on-content-click="false"
    v-model="menu"
    nudge-top="30"
    transition="scale-transition"
    offset-y
    :content-class="resolvedMenuDataTest"
    min-width="290px">
    <template v-slot:activator="{ on }">
      <fp-field :keys="$props">
        <v-text-field
            v-on="on"
            :class="{'fp-box-input-style': box, 'dirty-state': value && box, 'fp-input-dense': dense, 'fp-input-outline': outline}"
            :prepend-icon="prependIcon"
            :id="id"
            :data-test="resolvedDataTest"
            :outlined="outline"
            :hide-details="hideDetails"
            :filled="box"
            :value="displayValue"
            :label="label"
            :disabled="disabled"
            :rules="menu ? [] : rules"
            :required="required"
            :clearable="clearable"
            :single-line="dense"
            readonly
            :error="isError"
            :error-messages="errorMessages"
            @click:clear="clearCallback"/>
      </fp-field>
    </template>
    <v-date-picker
      v-model="model"
      :max="computedMaxDate"
      :min="computedMinDate"
      :first-day-of-week="1"
      :landscape="isMobile === false"
      ref="picker"
      :id="id ? `${id}-date-picker` : undefined"
      :locale="locale"
      @change="$emit('change', $event)"
    />
  </v-menu>
</template>

<script>
import FpValidationMixin from '@/components/fpComponents/services/validationMixin';
import {DateFormat} from '@/components/fpComponents/enums/FpConfigEnums';
import Vue from 'vue';
import moment from 'moment';
import {VMenu, VDatePicker, VTextField} from 'vuetify/lib';
import {resolveDataTest, resolveMenuDataTest} from './useDataTest';

export default {
  name: 'FpDatepicker',
  mixins: [FpValidationMixin,],
  components: {VMenu, VDatePicker, VTextField,},
  props: {
    dataTest: {
      type: String,
      default: undefined,
    },
    adultValidation: {
      type: Boolean,
      default: false,
    },
    iso: {
      type: Boolean,
      default: false,
    },
    prependIcon: {
      default: undefined,
      type: String,
    },
    dense: {
      default: false,
      type: Boolean,
    },
    hideDetails: {
      default: false,
      type: Boolean,
    },
    box: {
      default: () => false,
      type: Boolean,
    },
    outline: {
      type: Boolean,
      default: () => true,
    },
    returnType: {
      type: String,
      default: () => DateFormat.DATE_OBJECT,
    },
    displayFormat: {
      type: String,
      default: null,
    },
    locale: {
      type: String,
      default: (vue = Vue) => vue.prototype.$vuetify.lang.current || 'pl-PL',
    },
    minDate: {
      type: [Date, String],
      default: null,
    },
    maxDate: {
      type: [Date, String],
      default: null,
    },
    // przy ekspercie ustawić na YEAR
    startWith: {
      type: String,
      default: 'DAY',
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    endOfDay: {
      type: Boolean,
      default: false,
    },
    isError: {
      type: Boolean,
      default: false,
    },
    errorMessages: {
      type: [String, Array],
      default: () => [],
    },
  },
  data() {
    return {
      isMobile: window.innerWidth < 600,
      menu: false,
    };
  },
  computed: {
    resolvedDataTest() {
      return resolveDataTest(this.dataTest, this.id);
    },
    resolvedMenuDataTest() {
      return resolveMenuDataTest(this.dataTest, this.id);
    },
    model: {
      get() {
        const value = this.value;
        if (value && value !== '') {
          return moment(value instanceof Date ? value : new Date(value)).format('YYYY-MM-DD');
        } else {
          return null;
        }
      },
      set(value) {
        this.$refs.menu.save(value);
        if (this.returnType === DateFormat.DATE_OBJECT && value) {
          value = new Date(value);
          if (this.value && this.value instanceof Date) {
            value.setHours(this.value.getHours());
            value.setMinutes(this.value.getMinutes());
          } else {
            value.setHours(0);
            value.setMinutes(0);
          }

          value.setSeconds(0);
          if (this.endOfDay) {
            value = moment(value).set({hours: 23, minutes: 59, seconds: 59,}).toDate();
          }
          if (this.iso) {
            value = value.toISOString();
          }
        }
        this.$emit('input', value);
      },
    },
    displayValue() {
      return this.model && this.displayFormat ? moment(this.model, 'YYYY-MM-DD', true).format(this.displayFormat) : this.model;
    },
    computedMaxDate() {
      if (this.adultValidation) {
        const newDate = new Date();
        newDate.setFullYear(newDate.getFullYear() - 18);
        return newDate.toISOString().substr(0, 10);
      } else return this.maxDate ? moment(this.maxDate).format('yyyy-MM-DD') : '';
    },
    computedMinDate() {
      return this.minDate ? moment(this.minDate).format('yyyy-MM-DD') : '';
    },
  },
  watch: {
    menu(val) {
      val && this.startWith !== 'DAY' && this.$nextTick(() => (this.$refs.picker.activePicker = this.startWith));
    },
  },
  methods: {
    unMaskValue(v) {
      return this.displayFormat && v ? moment(v, this.displayFormat, true).format('YYYY-MM-DD') : v;
    },
    clearCallback() {
      this.$emit('input', null);
      this.$emit('click:clear');
    },
  },
};
</script>

<style scoped lang="scss">
  >>> label::after {
    content: '' !important;
  }
  .v-picker :deep(.v-picker__body) {
    background-color: var(--token-primary-surface);
    .v-date-picker-table__current {
      color: var(--token-primary-text) !important;
    }
  }
</style>
